import { Grid, useTheme, alpha } from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
} from 'recharts';
import {
    DarkMode as ThemeIcon,
    History as HistoryIcon,
    Tune as ForecastIcon,
} from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import type { MetricsData } from '@/types';
import {
    MetricsStatCard,
    MetricsChartCard,
    MetricsSectionHeading,
    PIE_COLORS,
    renderPieLabel,
    makeTooltipStyle,
} from './index';

interface Props {
    data: MetricsData;
}

export const MetricsUsagePatternsSection = ({ data }: Props) => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const tt = makeTooltipStyle(theme);
    const divider = alpha(theme.palette.divider, 0.5);
    const tickStyle = { fill: theme.palette.text.secondary, fontSize: 12 };
    const isFiltered = data.resourceId !== null;
    const filteredLabel = LL.metricsPage.filteredByUser();

    const historyPerDay = data.history.perDay.map(({ day, count }) => ({
        day: new Date(day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        count,
    }));

    const bucketColors: Record<string, string> = {
        last7d: theme.palette.success.main,
        last30d: theme.palette.info.main,
        last90d: theme.palette.warning.main,
        older: theme.palette.error.main,
    };

    const themeColors: Record<string, string> = {
        dark: theme.palette.primary.main,
        light: theme.palette.warning.main,
    };

    const bucketLabel = (key: string): string => {
        const map: Record<string, string> = {
            last7d: LL.metricsPage.charts.lastLoginLast7d(),
            last30d: LL.metricsPage.charts.lastLoginLast30d(),
            last90d: LL.metricsPage.charts.lastLoginLast90d(),
            older: LL.metricsPage.charts.lastLoginOlder(),
        };
        return map[key] ?? key;
    };

    const lastLoginData = (data.users.lastLoginBuckets ?? []).map((b) => ({
        label: bucketLabel(b.label),
        count: b.count,
        key: b.label,
    }));

    return (
        <MetricsSectionHeading
            icon={<ThemeIcon fontSize="small" />}
            title={LL.metricsPage.sections.usagePatterns()}
        >
            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                {/* ── Row 1: stat cards + pies ─────────────────────────── */}

                {/* Stat cards stacked in a single md:3 column */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Grid container spacing={2} sx={{ height: '100%' }}>
                        <Grid size={12}>
                            <MetricsStatCard
                                label={LL.metricsPage.stats.historyOpened()}
                                value={data.history.total}
                                icon={<HistoryIcon />}
                                color="secondary.main"
                                gradient={`linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.dark, 0.15)} 100%)`}
                                border={`1px solid ${alpha(theme.palette.secondary.main, 0.3)}`}
                                filtered={isFiltered}
                                filteredLabel={filteredLabel}
                            />
                        </Grid>
                        <Grid size={12}>
                            <MetricsStatCard
                                label={LL.metricsPage.stats.filterChanged()}
                                value={data.categoryFilter.total}
                                icon={<ForecastIcon />}
                                color="info.main"
                                gradient={`linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)} 0%, ${alpha(theme.palette.info.dark, 0.15)} 100%)`}
                                border={`1px solid ${alpha(theme.palette.info.main, 0.3)}`}
                                filtered={isFiltered}
                                filteredLabel={filteredLabel}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {/* ── Login recency — horizontal bar spanning md:6 ── */}
                {lastLoginData.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.lastLoginRecency()}
                            height={220}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={lastLoginData}
                                    layout="vertical"
                                    margin={{ left: 8, right: 32 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis type="number" allowDecimals={false} tick={tickStyle} />
                                    <YAxis
                                        type="category"
                                        dataKey="label"
                                        tick={{ ...tickStyle, fontSize: 11 }}
                                        width={130}
                                    />
                                    <Tooltip {...tt} />
                                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                                        {lastLoginData.map((entry) => (
                                            <Cell
                                                key={entry.key}
                                                fill={bucketColors[entry.key] ?? PIE_COLORS[0]}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* Language distribution pie — all users, so hidden under a user filter */}
                {!isFiltered && data.languages.length > 0 && (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.languageDistribution()}
                            height={220}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={data.languages}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={75}
                                        dataKey="count"
                                        nameKey="language"
                                        label={renderPieLabel}
                                        labelLine={false}
                                    >
                                        {data.languages.map((_, i) => (
                                            <Cell
                                                key={i}
                                                fill={PIE_COLORS[i % PIE_COLORS.length]}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip {...tt} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* ── Row 2: history + theme distribution ── */}

                {/* History per day */}
                {historyPerDay.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.historyUsage()}
                            height={240}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={historyPerDay}
                                    margin={{ left: 0, right: 16, top: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                    <XAxis
                                        dataKey="day"
                                        tick={{ ...tickStyle, fontSize: 10 }}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis allowDecimals={false} tick={tickStyle} />
                                    <Tooltip {...tt} />
                                    <Bar
                                        dataKey="count"
                                        fill={theme.palette.secondary.main}
                                        radius={[3, 3, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* Theme distribution — current pie chart */}
                {!isFiltered && data.theme.distribution.length > 0 && (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.themeDistribution()}
                            height={240}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={data.theme.distribution}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={75}
                                        dataKey="count"
                                        nameKey="theme"
                                        label={renderPieLabel}
                                        labelLine={false}
                                    >
                                        {data.theme.distribution.map((entry, i) => (
                                            <Cell
                                                key={i}
                                                fill={
                                                    themeColors[entry.theme] ??
                                                    PIE_COLORS[i % PIE_COLORS.length]
                                                }
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip {...tt} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </MetricsChartCard>
                    </Grid>
                )}
            </Grid>
        </MetricsSectionHeading>
    );
};
